<template>
	<div class="Autocomplete-parent">
		<div class="Autocomplete" v-if="items.length > 0">
			<div
				class="item"
				:class="{ isSelectedWithKeyboard: selectedIndex === index }"
				v-for="(item, index) in items"
				:key="'autocomplete-' + index"
				@mousedown.prevent.stop=""
				@click="onclick(item)"
			>
				<div class="item-primary">{{ item.text }}</div>
				<div class="item-secondary">{{ item.secondaryText }}</div>
			</div>
		</div>
	</div>
</template>

<script>
import { autocompletePlace, AUTOCOMPLETE_TYPE } from '../util/googleApis'
import { throttle } from '../util/limiter'

export default {
	name: 'Autocomplete',
	props: {
		inputElementQuery: {
			required: true,
			type: String,
		},
		inputRefName: {
			required: true,
			type: String,
			validator: (value) => value.length > 0,
		},
		// Type is one of https://developers.google.com/places/web-service/autocomplete#place_types
		// or '[custom_cities]' which means we be a bit of magic to get more accurate results
		autocompleteType: {
			required: true,
			type: String,
			validator: (value) => Object.values(AUTOCOMPLETE_TYPE).includes(value),
		},
	},
	data: () => ({
		inputComponent: null,
		lastAutocompleteValue: null,
		selectedIndex: -1,
		items: [
			/*
            {
                reference: reference to google maps api result
                value: 'what will be inserted to input',
				text: 'what is seen in autocomplete'
				secondaryText: 'gray text under text'
            }
            */
		],
	}),
	watch: {
		items() {
			this.selectedIndex = -1
		},
	},
	mounted() {
		this.inputComponent = this.$parent.$refs[this.inputRefName]
		if (!this.inputComponent) console.error('input component not found:', this.inputRefName)

		let onInput = throttle(async (value) => {
			// run every 500ms max

			let query = value

			if (!query || this.lastAutocompleteValue === value || !this.inputComponent.hasFocus) {
				this.items = []
				return
			}
			let state = this.$store.state

			if (this.autocompleteType === AUTOCOMPLETE_TYPE.VENUE) {
				query = state.city + ' ' + query
			}

			let results = await autocompletePlace(
				query,
				this.autocompleteType,
				state.country,
				new window.google.maps.LatLng(state.latitude || 60, state.longitude || 24)
			)

			if (this.autocompleteType === AUTOCOMPLETE_TYPE.CITY) {
				results = filterCityResults(results, query)
			} else if (this.autocompleteType === AUTOCOMPLETE_TYPE.VENUE) {
				results = filterVenueResults(results)
			}

			results = results.slice(0, 3)

			this.items = results.map((prediction) => {
				let mainText = prediction.structured_formatting.main_text
				let secondaryText = prediction.structured_formatting.secondary_text
				return {
					reference: prediction,
					text: mainText,
					secondaryText,
					value: prediction.structured_formatting.main_text,
					placeId: prediction.place_id,
				}
			})
		}, 500)

		this.inputComponent.$on('input', onInput)
		this.inputComponent.$on('blur', () => {
			this.items = []
		})
		this.inputComponent.$on('lazyValueChanged', onInput)

		this.inputComponent.$on('keydown', (e) => {
			if (e.keyCode === 40) {
				// arrow down
				this.selectedIndex = Math.min(this.selectedIndex + 1, this.items.length - 1)
			} else if (e.keyCode === 38) {
				// arrow up
				this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
			} else if (e.keyCode === 13) {
				// enter
				if (this.selectedIndex >= 0 && this.items.length > this.selectedIndex) {
					this.onclick(this.items[this.selectedIndex])
				} else if (this.selectedIndex === -1 && this.items.length >= 1) {
					this.onclick(this.items[0])
				}
			}
		})
	},
	computed: {},
	methods: {
		onclick(item) {
			this.lastAutocompleteValue = item.value
			this.inputComponent.blur()
			this.items = []
			this.inputComponent.value = item.value
			this.$emit('autocompleteSelected', item)
		},
	},
	components: {},
}

function filterCityResults(results, query) {
	query = query.trim().toLowerCase()
	let filteredResults = results.filter((result) => result.description.toLowerCase().includes(query))
	if (filteredResults.length >= 1) {
		results = filteredResults
	}

	return results
}

function filterVenueResults(results) {
	const acceptableTypes = [
		'restaurant',
		'food',
		'grocery_or_supermarket',
		'cafe',
		'bakery',
		'florist',
	]
	const filteredResults = results.filter((result) => {
		for (const type of result.types) {
			if (acceptableTypes.includes(type)) {
				return true
			}
		}
		return false
	})
	if (filteredResults.length >= 1) {
		results = filteredResults
	}

	return results
}
</script>

<style lang="stylus" scoped>
.Autocomplete-parent {
	height: 0;
	position:relative;
	top: -17px;
}
.Autocomplete {
	box-shadow: 1px 2px 12px 0 rgba(0, 0, 0, 0.16);
	z-index: 100;
	position: absolute
	top: 0;
	width: 100%;
	border-radius: 4px;
    overflow: hidden;
}

.item {
	padding: 18px 20px;
	cursor: pointer;
	background: white;
	z-index: 100;

	.item-primary {

	}
	.item-secondary {
		color: gray;
	}
}

.item + .item {
	border-top: 1px solid #dadada;
}

.item:hover {
	background: #f9f9f6;
}

.item.isSelectedWithKeyboard {
	background: #eee;
}
</style>
